import type Konva from 'konva';
import type { AreaFill } from './types';

/**
 * The fills a pen can lay into an area it encloses — the hatch of a Störung and
 * the stipple of a Humus.
 *
 * Both are repeating tiles handed to Konva as `fillPatternImage`, so the mark
 * stays an ordinary `Konva.Line`: dragging, the Transformer, the eraser's
 * `destination-out` and hit-testing all keep working without a custom `sceneFunc`.
 *
 * MEASURED AGAINST THE SURFACE, like every other length in a scene, so a fill
 * keeps its density on a phone and in the full-resolution export — which is why
 * the tiles are derived rather than shipped as assets.
 */

/** Distance between two hatch lines, as a fraction of the surface width. */
export const HATCH_SPACING = 0.0125;
/** Weight of one hatch line, likewise — floored at a pixel below. */
export const HATCH_LINE_WIDTH = 0.0015;

/**
 * The stipple grid. Wider than the hatch spacing because a tile carries TWO dots
 * rather than one line, which puts them about 0.011 of the surface apart — the
 * density the hatch lines have.
 */
export const STIPPLE_SPACING = 0.016;
/** Radius of one stipple dot, as a fraction of the surface width. */
export const STIPPLE_DOT_RADIUS = 0.0011;

/**
 * Degrees, clockwise. Konva turns the TILE rather than the marks inside it, which
 * is why the tiles below are drawn straight (see areaFillTile). The stipple is
 * turned too: off the axes it stops lining up with the edges of the drawing.
 */
export const AREA_FILL_ANGLE = 45;

/**
 * The fill a stored scene's one-letter code means (see SerializedStroke.f).
 *
 * Here rather than in scene.ts because the PDF's Legende reads the raw scene too,
 * and the two must not disagree: a fill the legend fails to decode resolves the
 * stroke to the WRONG pen — Humus and Befundkontur are the same ink, and only
 * the fill separates them.
 */
export function areaFillFromCode(code?: 'h' | 's'): AreaFill | undefined {
    if (code === 'h') {
        return 'hatch';
    }
    if (code === 's') {
        return 'stipple';
    }
    return undefined;
}

/** The code a fill is stored as — the inverse of {@link areaFillFromCode}. */
export function areaFillToCode(fill?: AreaFill): 'h' | 's' | undefined {
    if (fill === 'hatch') {
        return 'h';
    }
    if (fill === 'stipple') {
        return 's';
    }
    return undefined;
}

/**
 * Tiles already built, by fill, colour and size: without this, every pointermove
 * during a stroke would allocate a fresh canvas per mark. Capped rather than
 * unbounded, because dragging a window resizes the surface continuously and each
 * width is a key of its own.
 */
const tiles = new Map<string, HTMLCanvasElement>();
const MAX_TILES = 32;

/**
 * Tile pixels per surface pixel for a fill drawn at `ratio` device pixels per
 * surface pixel (screen ratio times zoom, or the export's own ratio). A tile is
 * a BITMAP: made at one pixel per surface pixel, zooming in only magnified it —
 * the hatch went broad and soft, the dots blocky. Whole steps, so a wheel zoom
 * does not build a tile for every notch.
 */
export function areaFillResolution(ratio: number): number {
    return Math.max(1, Math.ceil(ratio - 0.01));
}

/**
 * The repeating tile for one fill, in one colour, at one surface size, drawn
 * `resolution` tile pixels per surface pixel — Konva scales it back down by
 * `fillPatternScale` (see areaFillPattern).
 *
 * Both are drawn STRAIGHT and turned by `fillPatternRotation`: a tile of slanted
 * lines tiles seamlessly only at the few angles whose slope divides the tile.
 *
 * The hatch line sits at the tile's middle rather than its edge, so its full width
 * falls inside — half a line on the edge is clipped and the repeat shows a seam
 * every tile. The stipple's two dots sit on the diagonal for the same reason, and
 * because one dot per tile lands in a grid the eye picks out immediately.
 */
export function areaFillTile(
    fill: AreaFill,
    color: string,
    surfaceWidth: number,
    resolution = 1
): HTMLCanvasElement {
    const spacing = fill === 'stipple' ? STIPPLE_SPACING : HATCH_SPACING;
    const size = Math.max(3, Math.round(spacing * surfaceWidth)) * resolution;
    const key = `${fill}|${color}|${size}|${resolution}`;
    const cached = tiles.get(key);
    if (cached) {
        return cached;
    }
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (ctx && fill === 'stipple') {
        const radius =
            Math.max(0.5, STIPPLE_DOT_RADIUS * surfaceWidth) * resolution;
        ctx.fillStyle = color;
        for (const at of [0.25, 0.75]) {
            ctx.beginPath();
            ctx.arc(size * at, size * at, radius, 0, Math.PI * 2);
            ctx.fill();
        }
    } else if (ctx) {
        ctx.strokeStyle = color;
        ctx.lineWidth =
            Math.max(1, HATCH_LINE_WIDTH * surfaceWidth) * resolution;
        ctx.beginPath();
        ctx.moveTo(0, size / 2);
        ctx.lineTo(size, size / 2);
        ctx.stroke();
    }
    if (tiles.size >= MAX_TILES) {
        tiles.clear();
    }
    tiles.set(key, canvas);
    return canvas;
}

/**
 * The fill props for a Konva shape: the tile at `resolution`, scaled back to
 * surface pixels. `fillPatternImage` is declared
 * `HTMLImageElement | HTMLCanvasElement` on the property itself
 * (konva/lib/Shape.d.ts) and handed straight to `createPattern`, which takes any
 * `CanvasImageSource` — only the `ShapeConfig` type is narrower than the thing it
 * configures. The cast is that type's fault, not the value's.
 */
export function areaFillPattern(
    fill: AreaFill,
    color: string,
    surfaceWidth: number,
    resolution: number
): Pick<
    Konva.ShapeConfig,
    'fillPatternImage' | 'fillPatternScaleX' | 'fillPatternScaleY'
> {
    return {
        fillPatternImage: areaFillTile(
            fill,
            color,
            surfaceWidth,
            resolution
        ) as unknown as HTMLImageElement,
        fillPatternScaleX: 1 / resolution,
        fillPatternScaleY: 1 / resolution
    };
}
